import React, { useCallback, useEffect, useRef, useState } from "react";

import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { LoadSpinner } from "features/LoadSpinner";

import { AdminArea } from "components/Body/AdminArea";
import { Drawer } from "components/Drawer";

import { AttendantService } from "services/attendant";
import { getActualTime } from "services/utils/dateTime";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_ADMIN } from "utils/entities/permission";
import { stringFormat } from "utils/stringFormat";

import { CreateEditAttendant } from "./CreateEdit";

export interface AttendantInterface {
  id: number;
  customerId: number;
  attendantRoleId: number;
  active: boolean;
  name: string;
  foreignKey?: string;
  createIn: Date;
  updateIn: Date;
}

const Attendant: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [nameAttendant, setNameAttendant] = useState<string>("");
  const [documentAttendant, setDocumentAttendant] = useState<string>("");
  const [emailAttendant, setEmailAttendant] = useState<string>("");
  const [active, setActive] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [element, setElement] = useState<AttendantInterface>();
  const [situation, setSituation] = useState<any>(2);
  const [listAttendant, setListAttendant] = useState<any>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attendantRoleId, setAttendantRoleId] = useState<number>();
  const [attendantRoleName, setAttendantRoleName] = useState<string>("");
  const [fieldError, setFieldError] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { user } = useAppSelector((state) => state.user);
  const { globalConfigurations } = useSelector(
    (state: any) => state.configurations,
  );

  const { addToast } = useToast();

  useEffect(() => {
    const paginationList = Array.isArray(globalConfigurations)
      ? globalConfigurations?.find((x: any) => x?.name === "PaginationList")
      : null;
    const paginationValue = paginationList?.value
      ? parseInt(paginationList.value)
      : 10;
    setPageSize(paginationValue);
  }, [globalConfigurations]);

  const clear = () => {
    setIsEdit(false);
    setNameAttendant("");
    setAttendantRoleName("");
    setAttendantRoleId(0);
    setActive(true);
    setOpenModal(false);
    setFieldError(false);
  };

  const openModalCleared = () => {
    clear();
    setOpenModal(true);
  };

  const handleOpenDrawerWithPermission = async () => {
    try {
      await AttendantService.getAllPaginated({
        pageNumber: 1,
        pageSize: 1,
        active: situation,
      });
      openModalCleared();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        addToast({
          type: "error",
          title: t("common.accessDenied"),
          description: t("participants.messages.noPermissionCreate"),
        });
      } else {
        console.error("Erro ao validar permissão:", error);
      }
    }
  };

  useEffect(() => {
    setFieldError(false);
  }, [nameAttendant, attendantRoleId]);

  const emptyName = stringFormat.isEmptyOrSpaces(nameAttendant);
  const emptyDocument = stringFormat.isEmptyOrSpaces(documentAttendant);
  const emptyEmail = stringFormat.isEmptyOrSpaces(emailAttendant);

  const fetchIdRef = useRef(0);

  const getList = async (page?: number, searchTerm?: string) => {
    setLoading(true);
    const fetchId = ++fetchIdRef.current;

    try {
      const response = await AttendantService.getAllPaginated({
        pageNumber: (page ?? currentPage) + 1,
        pageSize,
        searchTerm: searchTerm ?? search,
        active: situation,
      });
      if (fetchId !== fetchIdRef.current) return;
      setListAttendant(
        response?.data?.data?.items ?? response?.data?.data ?? [],
      );
      setTotalCount(
        response?.data?.data?.totalCount ?? response?.data?.totalCount ?? 0,
      );
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as any).response?.status === "number"
      ) {
        const status = (error as any).response.status;

        if (status === 403) {
          addToast({
            type: "error",
            title: t("common.accessDenied"),
            description: t("participants.messages.noPermissionAccess"),
          });
        }
      }

      console.error("Erro ao carregar a lista de atendentes:", error);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const checkEditCreateAttendant = async () => {
    if (isEdit) {
      await updateAttendant();
    } else {
      await createAttendant();
    }
  };

  const createAttendant = async () => {
    const payload = {
      customerId: user?.customerId,
      active: active,
      name: nameAttendant,
      createIn: getActualTime(),
      updateIn: getActualTime(),
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
      document: documentAttendant,
      email: !emptyEmail ? emailAttendant : null,
    };

    if (
      emailAttendant?.trim()?.length &&
      !emailAttendant?.trim()?.includes("@")
    ) {
      addToast({
        type: "warning",
        title: t("administration.users.messages.invalidEmail"),
        description: t("participants.messages.invalidEmailDesc"),
      });
      setFieldError(true);
      return;
    }

    if (emptyName || emptyDocument) {
      setFieldError(true);
      addToast({
        type: "warning",
        title: t("participants.singular"),
        description: t("participants.messages.fillFields"),
      });
    } else {
      try {
        setLoading(true);
        await AttendantService.postNewAttendant(payload);
        addToast({
          type: "success",
          title: t("participants.messages.createdTitle"),
          description: t("participants.messages.created"),
        });
      } catch (error: any) {
        if (error?.response?.status === 403) {
          addToast({
            type: "error",
            title: t("common.accessDenied"),
            description: t("participants.messages.noPermissionCreate"),
          });
        } else {
          addToast({
            type: "error",
            title: t("participants.messages.createError"),
            description:
              error?.response?.data?.message || t("common.unknownError"),
          });
        }
      } finally {
        setLoading(false);
        await getList();
        clear();
      }
    }
  };

  const updateAttendant = async () => {
    const payloadUpdate = {
      id: element?.id,
      customerId: user?.customerId,
      active: active,
      name: nameAttendant,
      createIn: element?.createIn,
      updateIn: getActualTime(),
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
      document: documentAttendant,
      email: !emptyEmail ? emailAttendant : null,
    };
    if (
      emailAttendant?.trim()?.length &&
      !emailAttendant?.trim()?.includes("@")
    ) {
      addToast({
        type: "warning",
        title: t("administration.users.messages.invalidEmail"),
        description: t("participants.messages.invalidEmailDesc"),
      });
      setFieldError(true);
      return;
    }

    if (emptyName || emptyDocument) {
      setFieldError(true);
      addToast({
        type: "warning",
        title: t("participants.singular"),
        description: t("participants.messages.fillFields"),
      });
    } else {
      try {
        setLoading(true);
        await AttendantService.putNewAtendant(payloadUpdate);
        addToast({
          type: "success",
          title: t("participants.messages.updatedTitle"),
          description: t("participants.messages.updated"),
        });
      } catch (error: any) {
        if (error?.response?.status === 403) {
          addToast({
            type: "error",
            title: t("common.accessDenied"),
            description: t("participants.messages.noPermissionUpdate"),
          });
        } else {
          addToast({
            type: "error",
            title: t("participants.messages.updateError"),
            description:
              error?.response?.data?.message || t("common.unknownError"),
          });
        }
      } finally {
        setLoading(false);
        await getList();
        clear();
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      getList(0, value);
    }, 400),
    [situation, pageSize],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (text: string) => {
    setSearch(text);
    debouncedSearch(text);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getList(page);
  };

  const handleSituationChange = (value: any) => {
    setSituation(value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (pageSize > 0) {
      setCurrentPage(0);
      getList(0);
    }
  }, [situation, pageSize]);

  const serverPageCount = Math.ceil(totalCount / pageSize);

  const editAttendant = async (item: any) => {
    setElement(item);
    setNameAttendant(item?.name);
    setDocumentAttendant(item?.document);
    setEmailAttendant(item?.email);
    setActive(item?.active);
    setAttendantRoleId(item?.attendantRoleId);
    setIsEdit(true);
    setOpenModal(true);
  };

  return (
    <>
      <AdminArea
        loading={loading}
        title={t("participants.title")}
        InputSearchLabel={t("common.search")}
        InputSearchOnChange={handleSearchChange}
        searchText={search}
        list={listAttendant}
        headerAddButtonOnClick={handleOpenDrawerWithPermission}
        onClick={(item: any) => editAttendant(item)}
        setOpenModal={setOpenModal}
        onChangeSituation={handleSituationChange}
        permissionName={FORMS_ADMIN.ATTENDANT}
        headerTrashButtonOnClick={function (): unknown {
          throw new Error("Function not implemented.");
        }}
        serverSidePagination
        totalCount={totalCount}
        currentPage={currentPage}
        serverPageCount={serverPageCount}
        onPageChange={handlePageChange}
      />
      <Drawer
        open={openModal}
        title={isEdit ? t("participants.edit") : t("participants.create")}
        onClose={() => setOpenModal(false)}
        onApply={() => checkEditCreateAttendant()}
        paddingLeft={33}
        paddingRight={33}
      >
        <CreateEditAttendant
          onChangeName={setNameAttendant}
          onChangeDocument={setDocumentAttendant}
          onChangeEmail={setEmailAttendant}
          nameAttendant={nameAttendant}
          documentAttendant={documentAttendant}
          emailAttendant={emailAttendant}
          active={active}
          onChangeSwitch={() => setActive(!active)}
          setAttendantRoleDropSelected={setAttendantRoleId}
          attendantRoleName={attendantRoleName}
          fieldError={fieldError}
        />
      </Drawer>
      <LoadSpinner loading={false} />
    </>
  );
};

export default Attendant;
